import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import ApplicationLayoutModel, { ApplicationLayoutGroupModel } from '../../../../../shared/models/applications/api/ApplicationLayoutModel';
import TooltipWrapper from '../../../../../shared/components/general/TooltipWrapper';
import AssistantMoveDropdown from './AssistantMoveDropdown';
import { LocationsTitle } from './ApplicationDashboard.styles';
import { sidebarBodyTypography, sidebarCaptionTypography, sidebarFieldStyles, sidebarFieldFocusStyles, sidebarFocusStyles } from './AssistantSidebar.styles';
import { breakpoint_small } from '../../../../../shared/constants/breakpoints';
import { voicify_spark_darker, voicify_spark_darkest, voicify_spark_lighter, voicify_spark_lightest, color_shades_light, color_shades_darker } from '../../../../../shared/constants/colors';

interface AssistantGroupListProps {
    layout: ApplicationLayoutModel;
    visibleAppIds: Set<string>;
    selectedGroupId: string;
    isOrganizing: boolean;
    isSaving: boolean;
    tooltipStyle?: string;
    searchQuery?: string;
    organizationId?: string;
    onChange: (layout: ApplicationLayoutModel) => void;
    renderApp: (appId: string, selection?: AssistantMoveSelection) => React.ReactNode;
}

interface AssistantMoveSelection {
    selected: boolean;
    disabled: boolean;
    onToggle: (shiftKey?: boolean) => void;
}

const makeId = () => {
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes).map(byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const collapsedDropPrefix = 'collapsed-category:';

const AssistantGroupList: React.FC<AssistantGroupListProps> = ({ layout, visibleAppIds, selectedGroupId, isOrganizing, isSaving, tooltipStyle, searchQuery = '', organizationId, onChange, renderApp }) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
    const [moveSelection, setMoveSelection] = useState<Set<string>>(new Set());
    const [creatingMoveCategory, setCreatingMoveCategory] = useState(false);
    const [moveName, setMoveName] = useState('');
    const [moveError, setMoveError] = useState('');
    const selectionAnchor = useRef<string | null>(null);
    const clearMoveSelection = () => {
        setMoveSelection(new Set());
        setCreatingMoveCategory(false);
        setMoveName('');
        setMoveError('');
        selectionAnchor.current = null;
    };
    useEffect(() => {
        if (!isOrganizing) clearMoveSelection();
    }, [isOrganizing]);
    const otherPositionKey = organizationId ? `SPARK_OTHER_CATEGORY_POSITION:${organizationId}` : null;
    const [otherPosition, setOtherPosition] = useState<number | null>(() => {
        try {
            const saved = otherPositionKey ? window.localStorage.getItem(otherPositionKey) : null;
            const position = saved === null ? null : Number(saved);
            return Number.isInteger(position) && position >= 0 ? position : null;
        } catch {
            return null;
        }
    });
    const orderedGroups = [...layout.groups];
    const otherGroup = { id: 'ungrouped', name: 'Other', appIds: layout.ungroupedAppIds };
    orderedGroups.splice(otherPosition === null ? orderedGroups.length : Math.min(otherPosition, orderedGroups.length), 0, otherGroup);
    const query = isOrganizing ? '' : searchQuery.trim().toLowerCase();
    const hasUngroupedHeader = layout.groups.length > 0 || isOrganizing || !!query;
    const isCollapsed = (id: string) => !query && (id !== 'ungrouped' || hasUngroupedHeader) && collapsedIds.has(id);
    const toggleCollapsed = (id: string) => setCollapsedIds(previous => {
        const next = new Set(previous);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
    });

    const toggleMoveSelection = (appId: string, shiftKey = false) => {
        if (!isOrganizing || isSaving) return;
        const ids = orderedGroups.filter(group => !isCollapsed(group.id))
            .reduce((all, group) => all.concat(group.appIds.filter(id => visibleAppIds.has(id))), [] as string[]);
        const anchor = ids.indexOf(selectionAnchor.current);
        const index = ids.indexOf(appId);
        setMoveSelection(previous => {
            const next = new Set(previous);
            if (shiftKey && anchor >= 0 && index >= 0) {
                ids.slice(Math.min(anchor, index), Math.max(anchor, index) + 1).forEach(id => next.add(id));
            } else if (next.has(appId)) next.delete(appId);
            else next.add(appId);
            return next;
        });
        selectionAnchor.current = appId;
    };

    const moveSelected = (destinationId: string, newCategoryName?: string) => {
        if (!isOrganizing || isSaving) return;
        if (!newCategoryName && destinationId !== 'ungrouped' && !layout.groups.some(group => group.id === destinationId)) return;
        const appIds = orderedGroups.reduce((ids, group) => ids.concat(group.appIds.filter(id => moveSelection.has(id) && visibleAppIds.has(id))), [] as string[]);
        if (!appIds.length) return;
        const moving = new Set(appIds);
        const groups = layout.groups.map(group => ({ ...group, appIds: group.appIds.filter(id => group.id === destinationId || !moving.has(id)) }));
        const ungroupedAppIds = layout.ungroupedAppIds.filter(id => destinationId === 'ungrouped' || !moving.has(id));
        if (newCategoryName) groups.push({ id: destinationId, name: newCategoryName, appIds });
        else {
            const destination = destinationId === 'ungrouped' ? ungroupedAppIds : groups.find(group => group.id === destinationId).appIds;
            destination.push(...appIds.filter(id => !destination.includes(id)));
        }
        setCollapsedIds(previous => {
            const next = new Set(previous);
            next.delete(destinationId);
            return next;
        });
        onChange({ ...layout, groups, ungroupedAppIds });
        clearMoveSelection();
    };

    const createMoveCategory = (event: React.FormEvent) => {
        event.preventDefault();
        const trimmed = moveName.trim();
        if (!trimmed || trimmed.length > 80 || trimmed.toLowerCase() === 'other' || layout.groups.some(group => group.name.toLowerCase() === trimmed.toLowerCase())) {
            setMoveError('Enter a unique name of up to 80 characters.');
            return;
        }
        moveSelected(makeId(), trimmed);
    };

    const startNameEdit = (group?: ApplicationLayoutGroupModel, isUngrouped = false) => {
        setEditingId(isUngrouped ? 'ungrouped' : group?.id || 'new');
        setName(isUngrouped ? 'Other' : group?.name || '');
        setNameError('');
    };

    const saveName = () => {
        if (!editingId || isSaving || !isOrganizing) return;
        const trimmed = name.trim();
        if ((editingId === 'ungrouped' && trimmed === 'Other') ||
            layout.groups.some(group => group.id === editingId && group.name === trimmed) ||
            (editingId === 'new' && !trimmed)) {
            setEditingId(null);
            setNameError('');
            return;
        }
        if (!trimmed || trimmed.length > 80 || trimmed.toLowerCase() === 'other' || layout.groups.some(group => group.id !== editingId && group.name.toLowerCase() === trimmed.toLowerCase())) {
            setNameError('Enter a unique name of up to 80 characters.');
            return;
        }
        const groups = editingId === 'new' || editingId === 'ungrouped'
            ? [...layout.groups, { id: makeId(), name: trimmed, appIds: editingId === 'ungrouped' ? [...layout.ungroupedAppIds] : [] }]
            : layout.groups.map(group => group.id === editingId ? { ...group, name: trimmed } : group);
        onChange({ ...layout, groups, ungroupedAppIds: editingId === 'ungrouped' ? [] : layout.ungroupedAppIds });
        setEditingId(null);
        setNameError('');
    };

    const renderName = (id: string, label: string, count: number, group?: ApplicationLayoutGroupModel) => (
        <NameWrapper as="h4">
            {isOrganizing && id !== 'new' && (
                <CollapseIconButton
                    type="button"
                    aria-label={`${isCollapsed(id) ? 'Expand' : 'Collapse'} ${label}`}
                    aria-expanded={!isCollapsed(id)}
                    aria-controls={`assistant-category-${id}`}
                    onClick={() => toggleCollapsed(id)}
                >
                    <Chevron viewBox="0 0 16 16" $isCollapsed={isCollapsed(id)} aria-hidden="true">
                        <path d="M4 6l4 4 4-4" />
                    </Chevron>
                </CollapseIconButton>
            )}
            {isOrganizing && editingId === id ? (
                <NameInput
                    autoFocus
                    maxLength={80}
                    aria-label="Group name"
                    aria-invalid={!!nameError}
                    placeholder="Group name"
                    value={name}
                    disabled={isSaving}
                    onFocus={event => event.target.select()}
                    onChange={event => { setName(event.target.value); setNameError(''); }}
                    onBlur={saveName}
                    onKeyDown={event => {
                        if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                        if (event.key === 'Escape') { setEditingId(null); setNameError(''); }
                    }}
                />
            ) : isOrganizing ? (
                <TooltipWrapper text="Click to rename group" replacementContentStyle={tooltipStyle} place="top">
                    <NameButton type="button" disabled={isSaving} onClick={() => startNameEdit(group, id === 'ungrouped')} aria-label={`Rename ${label}`}>
                        {label}
                    </NameButton>
                </TooltipWrapper>
            ) : (
                <CollapseNameButton
                    type="button"
                    aria-label={`${isCollapsed(id) ? 'Expand' : 'Collapse'} ${label}`}
                    aria-expanded={!isCollapsed(id)}
                    aria-controls={`assistant-category-${id}`}
                    disabled={!!query}
                    onClick={() => toggleCollapsed(id)}
                >
                    <Chevron viewBox="0 0 16 16" $isCollapsed={isCollapsed(id)} aria-hidden="true">
                        <path d="M4 6l4 4 4-4" />
                    </Chevron>
                    <GroupName>{label}</GroupName>
                </CollapseNameButton>
            )}
            {id !== 'new' && (
                <TooltipWrapper text="Total assistants in this group" replacementContentStyle={tooltipStyle} place="top">
                    <Count aria-label={`${count} assistants in ${label}`}>({count})</Count>
                </TooltipWrapper>
            )}
            {isOrganizing && editingId === id && nameError && <ErrorText role="alert">{nameError}</ErrorText>}
        </NameWrapper>
    );

    const deleteGroup = (group: ApplicationLayoutGroupModel) => {
        setCollapsedIds(previous => {
            const next = new Set(previous);
            next.delete(group.id);
            next.delete('ungrouped');
            return next;
        });
        onChange({
            ...layout,
            groups: layout.groups.filter(item => item.id !== group.id),
            ungroupedAppIds: [...layout.ungroupedAppIds, ...group.appIds]
        });
    };

    const renderHeader = (id: string, content: React.ReactNode) => {
        if (!isOrganizing || !isCollapsed(id)) return <GroupHeader>{content}</GroupHeader>;
        return <Droppable droppableId={`${collapsedDropPrefix}${id}`} type="APP" isDropDisabled={isSaving}>
            {(provided, snapshot) => <GroupHeader
                ref={provided.innerRef}
                {...provided.droppableProps}
                $isDraggingOver={snapshot?.isDraggingOver}
            >
                {content}
                <CollapsedPlaceholder>{provided.placeholder}</CollapsedPlaceholder>
            </GroupHeader>}
        </Droppable>;
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination || !isOrganizing || isSaving) return;
        if (result.type === 'GROUP') {
            if (result.source.index === result.destination.index) return;
            const reordered = [...orderedGroups];
            const [group] = reordered.splice(result.source.index, 1);
            if (!group) return;
            reordered.splice(result.destination.index, 0, group);
            const nextOtherPosition = reordered.findIndex(item => item.id === 'ungrouped');
            setOtherPosition(nextOtherPosition);
            try {
                if (otherPositionKey) window.localStorage.setItem(otherPositionKey, String(nextOtherPosition));
            } catch {
                // Keep the position for this session if browser storage is unavailable.
            }
            const groups = reordered.filter(item => item.id !== 'ungrouped');
            if (groups.some((item, index) => item.id !== layout.groups[index].id)) onChange({ ...layout, groups });
            return;
        }
        const groups = layout.groups.map(group => ({ ...group, appIds: [...group.appIds] }));
        const ungroupedAppIds = [...layout.ungroupedAppIds];
        const list = (id: string) => id === 'ungrouped' ? ungroupedAppIds : groups.find(group => group.id === id)?.appIds;
        const source = list(result.source.droppableId);
        const isCollapsedTarget = result.destination.droppableId.startsWith(collapsedDropPrefix);
        const destinationId = isCollapsedTarget ? result.destination.droppableId.slice(collapsedDropPrefix.length) : result.destination.droppableId;
        const destination = list(destinationId);
        if (!source || !destination) return;
        if (source === destination && (isCollapsedTarget || result.source.index === result.destination.index)) return;
        const appId = source.filter(id => visibleAppIds.has(id))[result.source.index];
        if (!appId) return;
        source.splice(source.indexOf(appId), 1);
        const visibleDestinationIds = destination.filter(id => visibleAppIds.has(id));
        const nextVisibleId = visibleDestinationIds[result.destination.index];
        if (isCollapsedTarget) destination.push(appId);
        else if (nextVisibleId) destination.splice(destination.indexOf(nextVisibleId), 0, appId);
        else if (visibleDestinationIds.length > 0) destination.splice(destination.indexOf(visibleDestinationIds[visibleDestinationIds.length - 1]) + 1, 0, appId);
        else destination.push(appId);
        onChange({ ...layout, groups, ungroupedAppIds });
    };

    const renderList = (groupId: string, appIds: string[]) => {
        const shown = appIds.filter(id => visibleAppIds.has(id));
        return <Droppable droppableId={groupId} type="APP" isDropDisabled={!isOrganizing || isSaving || isCollapsed(groupId)}>
            {(provided, snapshot) => <List id={`assistant-category-${groupId}`} hidden={isCollapsed(groupId)} ref={provided.innerRef} {...provided.droppableProps} $isDraggingOver={snapshot?.isDraggingOver}>
                {isOrganizing && shown.length === 0 && <EmptyDropTarget>Drop assistants here</EmptyDropTarget>}
                {shown.map((appId, index) => <Draggable draggableId={appId} index={index} key={appId} isDragDisabled={!isOrganizing || isSaving}>
                    {drag => <AppRow ref={drag.innerRef} {...drag.draggableProps}>
                        {isOrganizing && (
                            <TooltipWrapper text="Drag to reorder or move assistant" replacementContentStyle={tooltipStyle} place="top">
                                <DragHandle {...drag.dragHandleProps} aria-label={`Move assistant ${appId}`}>⋮⋮</DragHandle>
                            </TooltipWrapper>
                        )}
                        <AppCard>{renderApp(appId, isOrganizing ? {
                            selected: moveSelection.has(appId),
                            disabled: isSaving,
                            onToggle: shiftKey => toggleMoveSelection(appId, shiftKey)
                        } : undefined)}</AppCard>
                    </AppRow>}
                </Draggable>)}
                {provided.placeholder}
            </List>}
        </Droppable>;
    };

    const matchesSearch = (label: string, appIds: string[]) => !query || label.toLowerCase().includes(query) || appIds.some(id => visibleAppIds.has(id));
    const showGroup = (group: ApplicationLayoutGroupModel) => (selectedGroupId === 'all' || selectedGroupId === group.id) && matchesSearch(group.name, group.appIds);
    const showUngrouped = (selectedGroupId === 'all' || selectedGroupId === 'ungrouped') && matchesSearch('Other', layout.ungroupedAppIds);
    const sectionIds = layout.groups.filter(showGroup).map(group => group.id);
    if (showUngrouped && hasUngroupedHeader && (layout.ungroupedAppIds.length > 0 || isOrganizing)) sectionIds.push('ungrouped');
    const setAllCollapsed = (collapse: boolean) => setCollapsedIds(previous => {
        const next = new Set(previous);
        sectionIds.forEach(id => { if (collapse) next.add(id); else next.delete(id); });
        return next;
    });

    return <div onKeyDown={event => {
        if (event.key === 'Escape' && !editingId && !isSaving) clearMoveSelection();
    }}>
        {isOrganizing && moveSelection.size > 0 && <BulkMoveBar aria-label="Move selected assistants">
            <BulkMoveActions>
                <span role="status">{moveSelection.size} selected</span>
                <AssistantMoveDropdown groups={orderedGroups} disabled={isSaving} onSelect={groupId => {
                    if (groupId === 'new') {
                        setCreatingMoveCategory(true);
                        setMoveError('');
                    } else moveSelected(groupId);
                }} />
                <Action type="button" disabled={isSaving} onClick={clearMoveSelection}>Clear</Action>
            </BulkMoveActions>
            {creatingMoveCategory && <MoveCategoryForm onSubmit={createMoveCategory}>
                <NameInput autoFocus aria-label="New group name" aria-invalid={!!moveError} placeholder="Group name" maxLength={80} value={moveName} disabled={isSaving} onChange={event => {
                    setMoveName(event.target.value);
                    setMoveError('');
                }} />
                <Action type="submit" disabled={isSaving || !moveName.trim()}>Create &amp; move</Action>
                {moveError && <ErrorText role="alert">{moveError}</ErrorText>}
            </MoveCategoryForm>}
        </BulkMoveBar>}
        {sectionIds.length > 0 && <CategoryControls role="group" aria-label="Group visibility">
            <CategoryControl type="button" onClick={() => setAllCollapsed(false)} disabled={sectionIds.every(id => !isCollapsed(id))}>Expand all</CategoryControl>
            <CategoryControl type="button" onClick={() => setAllCollapsed(true)} disabled={!!query || sectionIds.every(isCollapsed)} title={query ? 'Clear search to collapse groups' : undefined}>Collapse all</CategoryControl>
        </CategoryControls>}
        <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="groups" type="GROUP" isDropDisabled={!isOrganizing || isSaving}>
            {provided => <div ref={provided.innerRef} {...provided.droppableProps}>
                {orderedGroups.map((group, index) => <Draggable draggableId={`group-${group.id}`} index={index} key={group.id} isDragDisabled={!isOrganizing || isSaving}>
                    {drag => <Group ref={drag.innerRef} {...drag.draggableProps} style={{ ...drag.draggableProps.style, display: (group.id === 'ungrouped' ? showUngrouped && (layout.ungroupedAppIds.length > 0 || isOrganizing) : showGroup(group)) ? undefined : 'none' }}>
                        {(group.id !== 'ungrouped' || hasUngroupedHeader) &&
                        renderHeader(group.id, <>
                            {isOrganizing && (
                                <TooltipWrapper text="Drag to reorder group" replacementContentStyle={tooltipStyle} place="top">
                                    <DragHandle {...drag.dragHandleProps} aria-label={`Move group ${group.name}`}>⋮⋮</DragHandle>
                                </TooltipWrapper>
                            )}
                            {renderName(group.id, group.name, group.appIds.length, group.id === 'ungrouped' ? undefined : group)}
                            {isOrganizing && group.id !== 'ungrouped' && <GroupActions>
                                <Action disabled={isSaving} onClick={() => deleteGroup(group)} aria-label={`Delete ${group.name}`}>Delete</Action>
                            </GroupActions>}
                        </>)}
                        {renderList(group.id, group.appIds)}
                    </Group>}
                </Draggable>)}
                {provided.placeholder}
            </div>}
        </Droppable>
        {isOrganizing && <AddButton disabled={isSaving || editingId === 'new'} onClick={() => startNameEdit()}>+ Add group</AddButton>}
        {isOrganizing && editingId === 'new' && <Editor>
            {renderName('new', '', 0)}
        </Editor>}
    </DragDropContext></div>;
};

const Group = styled.div`margin-bottom: 12px;`;
const GroupHeader = styled.div<{ $isDraggingOver?: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;
    min-height: 24px;
    border-radius: 8px;
    background: ${props => props.$isDraggingOver ? voicify_spark_lightest : 'transparent'};
    box-shadow: ${props => props.$isDraggingOver ? `inset 0 0 0 1px ${voicify_spark_darker}` : 'none'};
    transition: background-color 0.2s, box-shadow 0.2s;
`;
const CollapsedPlaceholder = styled.div`display: none;`;
const GroupName = styled.span`min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const NameWrapper = styled(LocationsTitle)`display: flex; align-items: center; flex-wrap: wrap; gap: 4px; flex: 1; min-width: 0; text-transform: none;`;
const NameButton = styled.button`
    ${sidebarFocusStyles}
    min-width: 0;
    max-width: calc(100% - 40px);
    border: 0;
    border-radius: 6px;
    padding: 4px 6px;
    background: transparent;
    color: inherit;
    font: inherit;
    letter-spacing: inherit;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: text;
    transition: background-color 0.2s, color 0.2s;

    &:hover:not(:disabled), &:focus-visible {
        background: ${voicify_spark_lighter};
        color: ${voicify_spark_darkest};
    }
    &:disabled { cursor: default; opacity: 0.5; }
`;
const CollapseNameButton = styled(NameButton)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    border-radius: 8px;
    cursor: pointer;

    &:disabled { opacity: 1; }
`;
const CollapseIconButton = styled(NameButton)`display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; width: 20px; height: 24px; padding: 4px; cursor: pointer;`;
const Chevron = styled.svg<{ $isCollapsed: boolean }>`flex-shrink: 0; width: 12px; height: 12px; fill: none; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; transform: ${props => props.$isCollapsed ? 'rotate(-90deg)' : 'none'}; transition: transform 0.2s;`;
const Count = styled.span`flex-shrink: 0; color: inherit; font: inherit; cursor: help;`;
const GroupActions = styled.div`display: flex; gap: 2px;`;
const List = styled.div<{ $isDraggingOver?: boolean }>`
    min-height: 4px;
    border-radius: 8px;
    background: ${props => props.$isDraggingOver ? voicify_spark_lightest : 'transparent'};
    transition: background-color 0.2s;
`;
const EmptyDropTarget = styled.div`
    ${sidebarBodyTypography}
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    border: 1px dashed ${color_shades_light};
    border-radius: 8px;
    color: ${color_shades_darker};
    pointer-events: none;
`;
const AppRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    margin-bottom: 12px;

    ${breakpoint_small} { margin-bottom: 8px; }
    &:last-child { margin-bottom: 0; }
`;
const AppCard = styled.div`
    flex: 1;
    min-width: 0;

    && > * { margin-bottom: 0; }
`;
const DragHandle = styled.div`
    ${sidebarFocusStyles}
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 20px;
    min-height: 24px;
    border-radius: 4px;
    color: ${color_shades_darker};
    cursor: grab;
    touch-action: none;
    font-size: 16px;
    line-height: 1;

    &:hover { color: ${voicify_spark_darker}; background: ${voicify_spark_lightest}; }
    &:active { cursor: grabbing; }
`;
const Action = styled.button`
    ${sidebarCaptionTypography}
    ${sidebarFocusStyles}
    border: 0;
    border-radius: 4px;
    background: none;
    color: ${voicify_spark_darker};
    padding: 4px;
    cursor: pointer;

    &:hover:not(:disabled) { background: ${voicify_spark_lightest}; }
    &:disabled { color: ${color_shades_darker}; opacity: 0.5; cursor: default; }
`;
const CategoryControls = styled.div`display: flex; align-items: center; justify-content: flex-end; gap: 4px; margin-bottom: 4px;`;
const CategoryControl = Action;
const BulkMoveBar = styled.div`
    ${sidebarCaptionTypography}
    position: sticky;
    top: 0;
    z-index: 11;
    margin-bottom: 8px;
    padding: 6px 8px;
    border: 1px solid ${voicify_spark_lighter};
    border-radius: 8px;
    background: ${voicify_spark_lightest};
    color: ${voicify_spark_darkest};
`;
const BulkMoveActions = styled.div`display: flex; align-items: center; gap: 8px;`;
const MoveCategoryForm = styled.form`display: flex; align-items: center; flex-wrap: wrap; gap: 4px; margin-top: 6px;`;
const AddButton = styled(Action)`
    ${sidebarFieldStyles}
    width: 100%;
    text-align: left;
    border-style: dashed;
`;
const Editor = styled.div`display: flex; align-items: center; flex-wrap: wrap; gap: 4px; min-width: 0; margin: 8px 0 12px;`;
const NameInput = styled.input`
    ${sidebarFieldStyles}
    ${sidebarFieldFocusStyles}
    flex: 1;
    min-width: 0;
    width: 100%;
    max-width: 100%;
    margin: 0;
    color: inherit;
    font: inherit;

`;
const ErrorText = styled.span`${sidebarCaptionTypography} width: 100%; color: #b42318;`;

export default AssistantGroupList;
